import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, Grid, MenuItem,
    Select, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, TextField, Typography,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';
import PageTitle from '../../components/PageTitle';
import Loader from '../../common/Loader';
import KpiCard from './shared/KpiCard';
import { getDefaultDateRange, buildBaselineAndFilter, buildBaselineOrFilter } from './shared/constants';
import { formatDate1 } from '../../common/util';
import { apiConstants, postGeneric, getGeneric } from '../../common/constants';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { exportXlsx } from './shared/exportHelpers';
import PageBanner from './shared/PageBanner';

// Sample CSS replicas — match mockup `.filter-field input/select` exactly.
// Sample plain HTML input/select: 8px 12px padding, 12px font, 1px solid #ccc,
// 4px radius, white bg. Total height ~33px (8 + 16 line-height + 8 + 1×2 border).
const inputBaseSx = {
    '& .MuiOutlinedInput-root': {
        fontSize: '12px',
        borderRadius: '4px',
        backgroundColor: '#fff',
        minHeight: 0,
        // Hover/focus border colour: keep #ccc (sample doesn't change colour).
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1565c0', borderWidth: '1px' },
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 12px',
        fontSize: '12px',
        color: '#333',
        height: 'auto',
        lineHeight: '17px',
    },
    // Force Select to match input height (MUI Select gets extra min-height
    // by default which made it 38px while the TextField was 33px).
    '& .MuiSelect-select': {
        padding: '8px 32px 8px 12px !important',
        minHeight: '0 !important',
        height: 'auto',
        lineHeight: '17px',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ccc', top: 0, '& legend': { display: 'none' } },
    '& .MuiInputLabel-root': { display: 'none' },
    '& input::placeholder, & .MuiInputBase-input::placeholder': { opacity: '1 !important', color: '#999' },
    '& .MuiSelect-select .MuiTypography-root': { fontSize: '12px', color: '#999' },
};

// Sample `.btn .btn-blue` / `.btn-red` — solid colored, uppercase, 12px, 700 fw.
// Sample inline override: padding 7px 18px on filter/reset buttons.
const filterBtnSx = {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '7px 18px',
    minWidth: 0,
    boxShadow: 'none',
    borderRadius: '4px',
};

// Header cell style — sample table headers (11px uppercase #666 700).
const thSx = {
    py: '10px',
    px: '14px',
    fontSize: '11px',
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    bgcolor: '#fafafa',
    borderBottom: '2px solid #e0e0e0',
};

// History table headers — slightly smaller (10px).
const thSmallSx = { ...thSx, fontSize: '10px', px: '12px', py: '8px' };

// Body cells — sample 12px.
const tdSx = { px: '14px', py: '8px', fontSize: '12px', color: '#333', borderBottom: '1px solid #f0f0f0' };
const tdSmallSx = { ...tdSx, px: '12px' };

// Compact pagination for the perf summary tables.
const perfPaginationSx = {
    borderTop: '1px solid #eee',
    '& .MuiTablePagination-toolbar': { minHeight: 40 },
    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel, & .MuiTablePagination-select': {
        fontSize: '11px', color: '#666',
    },
};

// Inline rate chip (matches sample's bg-color span instead of MUI Chip).
function RateChip({ rate }) {
    const r = rate || 0;
    let bg = '#e8f5e9'; let color = '#2e7d32';
    if (r < 70) { bg = '#ffebee'; color = '#c62828'; }
    else if (r < 80) { bg = '#fff8e1'; color = '#e65100'; }
    return (
        <Box component='span' sx={{
            bgcolor: bg, color, px: '8px', py: '2px', borderRadius: '4px',
            fontSize: '11px', fontWeight: 600, display: 'inline-block',
        }}>{r}%</Box>
    );
}

export default function ABDConsigneeTagging() {
    const { enqueueSnackbar } = useSnackbar();
    const def = getDefaultDateRange();
    const [search, setSearch] = useState('');
    const [merchantOid, setMerchantOid] = useState('');
    const [city, setCity] = useState('');
    const [stateName, setStateName] = useState('');
    const [abdFilter, setAbdFilter] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [merchantOidApplied, setMerchantOidApplied] = useState('');
    const [cityApplied, setCityApplied] = useState('');
    const [stateApplied, setStateApplied] = useState('');
    const [abdFilterApplied, setAbdFilterApplied] = useState('');
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);

    // Client-side pagination for the perf summary tables (Client / Party / Channel). They arrive
    // together in one response, so paging them is just slicing arrays already in memory — no refetch.
    // Independent page per table, shared page size; changing size resets all to page 0.
    const [perfPageSize, setPerfPageSize] = useState(10);
    const [merchantPage, setMerchantPage] = useState(0);
    const [partyPage, setPartyPage] = useState(0);
    const [channelPage, setChannelPage] = useState(0);
    const onPerfRowsChange = (e) => {
        setPerfPageSize(parseInt(e.target.value, 10));
        setMerchantPage(0); setPartyPage(0); setChannelPage(0);
    };

    // Allowed ABD channels (managed in system defaults, read-only here) for the per-row dropdown.
    const [channels, setChannels] = useState([]);
    useEffect(() => {
        let alive = true;
        getGeneric(apiConstants.ABD_CHANNELS_GET)
            .then((res) => {
                if (alive) setChannels(res?.data?.responseObj?.channels || []);
            })
            .catch(() => { if (alive) setChannels([]); });
        return () => { alive = false; };
    }, []);

    // Global count of ABD-tagged consignees (isAbd=true) — the true KPI total, independent of the
    // 90-day window / current page. Re-fetched on each tag change (reload) so the card stays accurate.
    const [abdPartyCount, setAbdPartyCount] = useState(null);
    useEffect(() => {
        let alive = true;
        getGeneric(apiConstants.ABD_PARTY_COUNT)
            .then((res) => { if (alive) setAbdPartyCount(res?.data?.responseObj ?? null); })
            .catch(() => { if (alive) setAbdPartyCount(null); });
        return () => { alive = false; };
    }, [reload]);

    // Split into two dashboard calls so changing party search / abdFilter / page
    // doesn't refetch summary + buckets + perf (which only care about
    // merchantOidApplied + reload). Two parallel HTTP calls; cheaper than one
    // call that re-runs the whole $facet on every search keystroke.
    const { data: dash, loading } = useAppointmentDashboard({
        filters: { fromDate: def.fromDate, toDate: def.toDate, merchant: merchantOidApplied },
        include: ['buckets', 'summary', 'perf'],
        groupBy: ['merchant', 'party', 'channel'],
        reload,
    });
    const { data: partiesDash, loading: partiesLoading } = useAppointmentDashboard({
        filters: {
            fromDate: def.fromDate, toDate: def.toDate,
            merchant: merchantOidApplied,
            city: cityApplied,
            state: stateApplied,
        },
        include: ['parties'],
        partiesPage: page,
        partiesSize: sizePerPage,
        partiesSearch: searchApplied,
        partiesAbdFilter: abdFilterApplied,
        reload,
    });

    const parties = partiesDash?.parties?.data || [];
    const total = partiesDash?.parties?.total || 0;
    const merchantHistory = dash?.perf?.merchant || [];
    const partyHistory = dash?.perf?.party || [];
    const channelHistory = dash?.perf?.channel || [];
    // Channels are { code, label }; party.abdChannel and perf.channel rows carry the code.
    const channelLabel = (code) => {
        if (!code) return '';
        const m = channels.find((c) => c.code === code);
        return m ? m.label : code;
    };

    // Page slices for the perf tables + spacer height so every page renders the same height
    // (a partial last page otherwise shrinks the card and unbalances the side-by-side layout).
    const PERF_ROW_H = 34; // px per perf-table body row
    const sliceP = (arr, pg) => arr.slice(pg * perfPageSize, pg * perfPageSize + perfPageSize);
    const merchantRows = sliceP(merchantHistory, merchantPage);
    const partyRows = sliceP(partyHistory, partyPage);
    const channelRows = sliceP(channelHistory, channelPage);
    // Filler height applies only when the table is paginated (more than one page exists).
    const fillerH = (rows, fullLen) => (fullLen > perfPageSize ? (perfPageSize - rows.length) * PERF_ROW_H : 0);

    // Clamp a perf table's page when its row count shrinks (e.g. after a toggle/reload) so we never
    // land on a now-empty page past the end.
    useEffect(() => {
        const clamp = (len, pg, set) => {
            const maxPage = Math.max(0, Math.ceil(len / perfPageSize) - 1);
            if (pg > maxPage) set(maxPage);
        };
        clamp(merchantHistory.length, merchantPage, setMerchantPage);
        clamp(partyHistory.length, partyPage, setPartyPage);
        clamp(channelHistory.length, channelPage, setChannelPage);
    }, [merchantHistory.length, partyHistory.length, channelHistory.length,
        perfPageSize, merchantPage, partyPage, channelPage]);

    const togglePartyAbd = async (party, nextValue) => {
        const action = nextValue ? 'Enable' : 'Disable';
        const confirmed = window.confirm(
            `${action} ABD tagging for "${party.partyName}"?\n\n`
            + 'This affects all future B2B orders for this consignee. '
            + 'Existing scheduled appointments are not impacted.'
        );
        if (!confirmed) return;
        try {
            await postGeneric(apiConstants.ABD_PARTY_TOGGLE, {
                items: [{ partyName: party.partyName, isAbd: nextValue }],
            });
            enqueueSnackbar(`ABD ${nextValue ? 'enabled' : 'disabled'} for ${party.partyName}`, { variant: 'success' });
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to update', { variant: 'error' });
        }
    };

    const tagPartyChannel = async (party, nextChannel) => {
        // Confirm every channel change (set / change / clear), mirroring the ABD toggle guardrail.
        const confirmed = window.confirm(
            nextChannel
                ? `Set ABD channel to "${channelLabel(nextChannel)}" for "${party.partyName}"?\n\n`
                    + 'This channel will be applied to all future B2B orders for this consignee. '
                    + 'Existing orders are not changed.'
                : `Clear the ABD channel for "${party.partyName}"?`,
        );
        if (!confirmed) return;
        try {
            await postGeneric(apiConstants.ABD_PARTY_CHANNEL_TAG, {
                items: [{ partyName: party.partyName, abdChannel: nextChannel || '' }],
            });
            enqueueSnackbar(
                nextChannel
                    ? `Channel "${channelLabel(nextChannel)}" set for ${party.partyName}`
                    : `Channel cleared for ${party.partyName}`,
                { variant: 'success' },
            );
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to update channel', { variant: 'error' });
        }
    };

    const totalAbdParties = parties.filter((p) => p.isAbd).length;

    const partyToRow = (p) => ({
        'Party Name': p.partyName,
        'Address': p.address || '',
        'City': p.city || '',
        'Pincode': p.pincode || '',
        'Tagged By': p.taggedBy || '',
        'Tagged On': p.taggedAt ? formatDate1(p.taggedAt, 'dd MMM, yyyy') : '',
        'Total Shipments': p.totalShipments ?? '',
        'Avg Attempts': p.avgAttempts ? Number(p.avgAttempts).toFixed(1) : '',
        'ABD Flag': p.isAbd ? 'Enabled' : 'Disabled',
        'Channel': channelLabel(p.abdChannel),
    });

    const [exporting, setExporting] = useState(false);
    // Export ALL filtered consignees (every page), not just the page on screen. Pages the parties
    // endpoint at 200/call using the same applied filters, so the file matches the on-screen list.
    const exportAbdList = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            const filterArgs = {
                fromDate: def.fromDate, toDate: def.toDate,
                merchant: merchantOidApplied, city: cityApplied, state: stateApplied,
            };
            const baseline = {
                andfilter: buildBaselineAndFilter(filterArgs),
                orfilter: buildBaselineOrFilter(filterArgs),
            };
            const summaryScope = {
                merchantOid: merchantOidApplied || undefined,
                city: cityApplied || undefined,
                state: stateApplied || undefined,
            };
            const PAGE = 200;
            const collected = [];
            let pageIdx = 0;
            let totalRows = Infinity;
            enqueueSnackbar('Preparing export…', { variant: 'info' });
            while (collected.length < totalRows && pageIdx < 200) {
                // eslint-disable-next-line no-await-in-loop
                const res = await postGeneric(apiConstants.APPOINTMENT_DELIVERY_DASHBOARD, {
                    baseline,
                    summaryScope,
                    include: ['parties'],
                    partiesPage: pageIdx,
                    partiesSize: PAGE,
                    partiesSearch: searchApplied || undefined,
                    partiesAbdFilter: abdFilterApplied || undefined,
                });
                const block = res?.data?.responseObj?.parties || {};
                totalRows = block.total || 0;
                const data = block.data || [];
                collected.push(...data);
                if (data.length === 0) break;
                pageIdx += 1;
            }
            exportXlsx('ABD_Consignee_List', [{ name: 'ABD Consignees', rows: collected.map(partyToRow) }]);
            enqueueSnackbar(`Exported ${collected.length} consignees`, { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Export failed', { variant: 'error' });
        } finally {
            setExporting(false);
        }
    };
    const exportClientHistory = () => {
        const rows = merchantHistory.map((m) => ({
            Client: m.label,
            'ABD Shipments': m.total || 0,
            Delivered: m.delivered || 0,
            'Rate %': m.deliveryRate || 0,
        }));
        exportXlsx('ABD_Client_History', [{ name: 'Client History', rows }]);
    };
    const exportPartyHistory = () => {
        const rows = partyHistory.map((p) => ({
            Party: p.label,
            Shipments: p.total || 0,
            Delivered: p.delivered || 0,
            'Avg Attempts': p.avgAttempts ? Number(p.avgAttempts).toFixed(1) : '',
        }));
        exportXlsx('ABD_Party_History', [{ name: 'Party History', rows }]);
    };
    const exportChannelHistory = () => {
        const rows = channelHistory.map((c) => ({
            Channel: channelLabel(c.label),
            'ABD Shipments': c.total || 0,
            Delivered: c.delivered || 0,
            'Rate %': c.deliveryRate || 0,
            'Avg Attempts': c.avgAttempts ? Number(c.avgAttempts).toFixed(1) : '',
        }));
        exportXlsx('ABD_Channel_History', [{ name: 'Channel History', rows }]);
    };

    const kpi = {
        // Global ABD-consignee count from the dedicated endpoint; fall back to the page-local
        // count only until that resolves.
        totalAbd: abdPartyCount ?? totalAbdParties,
        activeShipments: dash.abdActiveOpen || 0,
        deliveriesThisMonth: dash.abdLast3m || 0,
        pending: dash.needsScheduling || 0,
    };

    const onApplyFilter = () => {
        setSearchApplied(search);
        setMerchantOidApplied(merchantOid);
        setCityApplied(city);
        setStateApplied(stateName);
        setAbdFilterApplied(abdFilter);
        setPage(0);
        setReload((r) => r + 1);
    };
    const onResetFilter = () => {
        setSearch(''); setMerchantOid(''); setCity(''); setStateName(''); setAbdFilter('');
        setSearchApplied(''); setMerchantOidApplied('');
        setCityApplied(''); setStateApplied(''); setAbdFilterApplied('');
        setPage(0); setReload((r) => r + 1);
    };

    return (
        <>
            <PageTitle>ABD Consignee Tagging</PageTitle>
            {/* Full-screen loader only on the very first load — pagination/toggle refetches keep the
                grid in place rather than flashing the whole page. */}
            {(loading || partiesLoading) && parties.length === 0
                && merchantHistory.length === 0 && partyHistory.length === 0 && <Loader />}

            <PageBanner variant='info' icon={<ShieldIcon />} title='ABD Consignee Tagging — Flag Locations That Require Appointment-Based Delivery'>
                Tag consignees (Sold To Party) that require appointment-based delivery. Once tagged, all <strong>future</strong> shipments
                to that party will automatically enter the appointment workflow and appear under &quot;Needs Scheduling&quot; until a date is booked.
                In-flight ABD shipments are not affected when ABD is disabled.
            </PageBanner>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <KpiCard color='purple' label='Total ABD Consignees' value={kpi.totalAbd} sub='Tagged locations' icon={<ShieldIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='blue' label='Active ABD Shipments' value={kpi.activeShipments} sub='Currently in-transit' icon={<LocalShippingIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='green' label='ABD Deliveries (3 months)' value={kpi.deliveriesThisMonth} sub='Rolling window' icon={<CheckCircleIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='red' label='Pending Appointments' value={kpi.pending} sub='ABD shipments, no appointment' icon={<ErrorOutlineIcon />} />
                </Grid>
            </Grid>

            {/* Auto-Flag tip banner — matches sample's purple inline strip */}
            <Box sx={{
                bgcolor: '#f3e5f5',
                border: '1px solid #e1bee7',
                borderRadius: '6px',
                px: 1.75, py: 1.25,
                mb: 1.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '11px',
                color: '#6a1b9a',
            }}>
                <FlashOnIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                <Box sx={{ fontSize: '11px' }}>
                    <strong>Auto-Flag:</strong> When you enable ABD for a consignee below, all future shipments matching{' '}
                    <Box component='code' sx={{ bgcolor: '#fff', px: 0.5, borderRadius: '3px', fontSize: '11px' }}>soldToPartyName</Box>
                    {' '}will automatically enter the appointment workflow and show &quot;Needs Scheduling.&quot;
                </Box>
            </Box>

            {/* Filters row — sample CSS:
                  background:#fff; border-radius:8px;
                  padding:14px 18px; margin-bottom:14px;
                  display:flex; flex-wrap:wrap; gap:10px;
                  align-items:center;
                  box-shadow:0 1px 3px rgba(0,0,0,0.04); */}
            <Box sx={{
                bgcolor: '#fff',
                borderRadius: '8px',
                padding: '14px 18px',
                mb: '14px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            }}>
                <TextField
                    size='small'
                    placeholder='Search Party Name…'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...inputBaseSx, width: 200 }}
                />
                <TextField
                    size='small'
                    placeholder='City'
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...inputBaseSx, width: 140 }}
                />
                <TextField
                    size='small'
                    placeholder='State'
                    value={stateName}
                    onChange={(e) => setStateName(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...inputBaseSx, width: 140 }}
                />
                <Select
                    size='small'
                    displayEmpty
                    value={abdFilter}
                    onChange={(e) => setAbdFilter(e.target.value)}
                    sx={{ ...inputBaseSx, width: 160 }}
                >
                    <MenuItem value=''>ABD: All</MenuItem>
                    <MenuItem value='true'>ABD: Enabled</MenuItem>
                    <MenuItem value='false'>ABD: Disabled</MenuItem>
                </Select>
                <Button
                    variant='contained' onClick={onApplyFilter} disableElevation
                    sx={{ ...filterBtnSx, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' } }}
                >Filter</Button>
                <Button
                    variant='contained' onClick={onResetFilter} disableElevation
                    sx={{ ...filterBtnSx, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626', boxShadow: 'none' } }}
                >Reset</Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant='outlined' size='small'
                    disabled={exporting}
                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={exportAbdList}
                    sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '7px 14px',
                        color: '#1565c0',
                        bgcolor: 'transparent',
                        borderColor: '#1565c0',
                        borderRadius: '4px',
                        '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' },
                    }}
                >{exporting ? 'Exporting…' : 'Export ABD List'}</Button>
            </Box>

            <Typography sx={{ fontSize: '11px', color: '#999', mb: 1.25, mt: '-6px' }}>
                Filters apply to the consignee list below; the analytics tables show the overall 90-day window.
            </Typography>

            {/* Sold To Party Master — sample table styling */}
            <Card sx={{ mb: 2.5, borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{
                    px: 2.25, py: 1.75,
                    borderBottom: '1px solid #eee',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1a1a2e' }}>
                        Sold To Party Master — Tag Consignees That Need Appointment-Based Delivery
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>
                        {total.toLocaleString()} parties{abdFilterApplied === 'true' ? ' tagged as ABD' : ''}
                        {' · '}{totalAbdParties} ABD on this page
                    </Typography>
                </Box>
                <TableContainer sx={{ maxHeight: 560 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={thSx}>Party Name</TableCell>
                                <TableCell sx={thSx}>Address</TableCell>
                                <TableCell sx={thSx}>City</TableCell>
                                <TableCell sx={thSx}>Pincode</TableCell>
                                <TableCell sx={thSx}>Tagged By</TableCell>
                                <TableCell sx={thSx}>Tagged On</TableCell>
                                <TableCell sx={{ ...thSx, textAlign: 'center' }} align='center'>Total Shipments</TableCell>
                                <TableCell sx={{ ...thSx, textAlign: 'center' }} align='center'>Avg Attempts</TableCell>
                                <TableCell sx={{ ...thSx, textAlign: 'center' }} align='center'>ABD Flag</TableCell>
                                <TableCell sx={{ ...thSx, textAlign: 'center' }} align='center'>Channel</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {parties.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={10} align='center' sx={{ py: 3, color: '#888', fontSize: '12px' }}>No parties found</TableCell>
                                </TableRow>
                            )}
                            {parties.map((p) => (
                                <TableRow key={p.partyName} hover>
                                    <TableCell sx={{ ...tdSx, fontWeight: 600 }}>{p.partyName}</TableCell>
                                    <TableCell sx={tdSx} title={p.address || ''}>{p.address || '—'}</TableCell>
                                    <TableCell sx={tdSx}>{p.city || '—'}</TableCell>
                                    <TableCell sx={tdSx}>{p.pincode || '—'}</TableCell>
                                    <TableCell sx={tdSx}>{p.taggedBy || '—'}</TableCell>
                                    <TableCell sx={tdSx}>{p.taggedAt ? formatDate1(p.taggedAt, 'dd MMM, yyyy') : '—'}</TableCell>
                                    <TableCell sx={tdSx} align='center'>{p.totalShipments ?? '—'}</TableCell>
                                    <TableCell sx={tdSx} align='center'>{p.avgAttempts ? Number(p.avgAttempts).toFixed(1) : '—'}</TableCell>
                                    <TableCell sx={tdSx} align='center'>
                                        <Switch
                                            checked={!!p.isAbd}
                                            onChange={(e) => togglePartyAbd(p, e.target.checked)}
                                            size='small'
                                            color='primary'
                                            inputProps={{ 'aria-label': `ABD tagging for ${p.partyName}` }}
                                        />
                                    </TableCell>
                                    <TableCell sx={tdSx} align='center'>
                                        {/* Channel is managed independently of the ABD flag — set/clear it any time. */}
                                        <Select
                                            size='small'
                                            displayEmpty
                                            value={p.abdChannel || ''}
                                            onChange={(e) => tagPartyChannel(p, e.target.value)}
                                            sx={{ ...inputBaseSx, minWidth: 140 }}
                                            inputProps={{ 'aria-label': `ABD channel for ${p.partyName}` }}
                                        >
                                            <MenuItem value=''><em>— None —</em></MenuItem>
                                            {channels.map((c) => (
                                                <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
                                            ))}
                                            {/* Keep an out-of-list value visible rather than blanking the cell. */}
                                            {p.abdChannel && !channels.some((c) => c.code === p.abdChannel) && (
                                                <MenuItem key={p.abdChannel} value={p.abdChannel}>{p.abdChannel}</MenuItem>
                                            )}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component='div'
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={sizePerPage}
                    onRowsPerPageChange={(e) => { setSizePerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                    sx={{
                        borderTop: '1px solid #eee',
                        '& .MuiTablePagination-toolbar': { minHeight: 44 },
                        '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': { fontSize: '12px', color: '#666' },
                    }}
                />
            </Card>

            {/* History tables — Client + Party side-by-side */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', minHeight: 460, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <Box sx={{
                            px: 2.25, py: 1.75,
                            borderBottom: '1px solid #eee',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1a1a2e' }}>
                                ABD History — By Client (Merchant)
                            </Typography>
                            <Button
                                variant='outlined' size='small'
                                onClick={exportClientHistory}
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '5px 12px',
                                    color: '#1565c0',
                                    bgcolor: 'transparent',
                                    borderColor: '#1565c0',
                                    borderRadius: '4px',
                                    minWidth: 0,
                                    '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' },
                                }}
                            >Export</Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                        <TableCell sx={thSmallSx}>Client</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>ABD Shipments</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Delivered</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {merchantHistory.length === 0 && (
                                        <TableRow><TableCell colSpan={4} align='center' sx={{ color: '#888', py: 2, fontSize: '12px' }}>No data</TableCell></TableRow>
                                    )}
                                    {merchantRows.map((m) => (
                                        <TableRow key={m.key || m.label}>
                                            <TableCell sx={{ ...tdSmallSx, fontWeight: 600 }}>{m.label}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>{m.total}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>{m.delivered}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>
                                                <RateChip rate={m.deliveryRate} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {fillerH(merchantRows, merchantHistory.length) > 0 && (
                                        <TableRow style={{ height: fillerH(merchantRows, merchantHistory.length) }}>
                                            <TableCell colSpan={4} sx={{ borderBottom: 'none' }} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {merchantHistory.length > perfPageSize && (
                            <TablePagination
                                component='div'
                                count={merchantHistory.length}
                                page={merchantPage}
                                onPageChange={(_, p) => setMerchantPage(p)}
                                rowsPerPage={perfPageSize}
                                onRowsPerPageChange={onPerfRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                sx={perfPaginationSx}
                            />
                        )}
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', minHeight: 460, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <Box sx={{
                            px: 2.25, py: 1.75,
                            borderBottom: '1px solid #eee',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1a1a2e' }}>
                                ABD History — By Billed Party (Consignee)
                            </Typography>
                            <Button
                                variant='outlined' size='small'
                                onClick={exportPartyHistory}
                                sx={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '5px 12px',
                                    color: '#1565c0',
                                    bgcolor: 'transparent',
                                    borderColor: '#1565c0',
                                    borderRadius: '4px',
                                    minWidth: 0,
                                    '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' },
                                }}
                            >Export</Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                                        <TableCell sx={thSmallSx}>Party</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Shipments</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Delivered</TableCell>
                                        <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Avg Attempts</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {partyHistory.length === 0 && (
                                        <TableRow><TableCell colSpan={4} align='center' sx={{ color: '#888', py: 2, fontSize: '12px' }}>No data</TableCell></TableRow>
                                    )}
                                    {partyRows.map((p) => (
                                        <TableRow key={p.key || p.label}>
                                            <TableCell sx={{ ...tdSmallSx, fontWeight: 600 }}>{p.label}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>{p.total}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>{p.delivered}</TableCell>
                                            <TableCell sx={tdSmallSx} align='center'>
                                                {p.avgAttempts ? Number(p.avgAttempts).toFixed(1) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {fillerH(partyRows, partyHistory.length) > 0 && (
                                        <TableRow style={{ height: fillerH(partyRows, partyHistory.length) }}>
                                            <TableCell colSpan={4} sx={{ borderBottom: 'none' }} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {partyHistory.length > perfPageSize && (
                            <TablePagination
                                component='div'
                                count={partyHistory.length}
                                page={partyPage}
                                onPageChange={(_, p) => setPartyPage(p)}
                                rowsPerPage={perfPageSize}
                                onRowsPerPageChange={onPerfRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                sx={perfPaginationSx}
                            />
                        )}
                    </Card>
                </Grid>
                {/* Channel-wise ABD performance — grouped by the channel tagged on each party */}
                <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%', minHeight: 460, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <Box sx={{
                    px: 2.25, py: 1.75,
                    borderBottom: '1px solid #eee',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1a1a2e' }}>
                        ABD Performance — By Channel
                    </Typography>
                    <Button
                        variant='outlined' size='small'
                        onClick={exportChannelHistory}
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '5px 12px',
                            color: '#1565c0',
                            bgcolor: 'transparent',
                            borderColor: '#1565c0',
                            borderRadius: '4px',
                            minWidth: 0,
                            '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' },
                        }}
                    >Export</Button>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={thSmallSx}>Channel</TableCell>
                                <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>ABD Shipments</TableCell>
                                <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Delivered</TableCell>
                                <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Rate</TableCell>
                                <TableCell sx={{ ...thSmallSx, textAlign: 'center' }} align='center'>Avg Attempts</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {channelHistory.length === 0 && (
                                <TableRow><TableCell colSpan={5} align='center' sx={{ color: '#888', py: 2, fontSize: '12px' }}>No channel-tagged ABD shipments in this window</TableCell></TableRow>
                            )}
                            {channelRows.map((c) => (
                                <TableRow key={c.key || c.label} hover>
                                    <TableCell sx={{ ...tdSmallSx, fontWeight: 600 }}>{channelLabel(c.label)}</TableCell>
                                    <TableCell sx={tdSmallSx} align='center'>{c.total}</TableCell>
                                    <TableCell sx={tdSmallSx} align='center'>{c.delivered}</TableCell>
                                    <TableCell sx={tdSmallSx} align='center'><RateChip rate={c.deliveryRate} /></TableCell>
                                    <TableCell sx={tdSmallSx} align='center'>
                                        {c.avgAttempts ? Number(c.avgAttempts).toFixed(1) : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {fillerH(channelRows, channelHistory.length) > 0 && (
                                <TableRow style={{ height: fillerH(channelRows, channelHistory.length) }}>
                                    <TableCell colSpan={5} sx={{ borderBottom: 'none' }} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {channelHistory.length > perfPageSize && (
                    <TablePagination
                        component='div'
                        count={channelHistory.length}
                        page={channelPage}
                        onPageChange={(_, p) => setChannelPage(p)}
                        rowsPerPage={perfPageSize}
                        onRowsPerPageChange={onPerfRowsChange}
                        rowsPerPageOptions={[10, 25, 50]}
                        sx={perfPaginationSx}
                    />
                )}
                </Card>
                </Grid>
            </Grid>
        </>
    );
}

